/**
 * Rules and option lists for the EIA Manage Portfolio form — Portfolio Details
 * and Add Instance. Pure functions and constants, so every rule is testable
 * without rendering.
 *
 * Ported from legacy views/ManagePortfolio/ManageAccount.vue and
 * UploadDownloadTemplate.vue (EIA branch). Line references point there.
 */
import eiaSampleTemplate from '@/assets/templates/EIA_Sample_template.xlsx';
import eiaSelfPerfTemplate from '@/assets/templates/EIA_Self_Perf_template.xlsx';
import ociSelfPerfTemplate from '@/assets/templates/OCI_Self_perf_assessment.xlsx';
import ociTemplate from '@/assets/templates/OCI_Template_EIA.xlsx';

/* --- Portfolio Details ---------------------------------------------------- */

/**
 * Operational Safety Margin (legacy `headroom`): 0–100 in steps of 10,
 * starting at 20 (legacy store `headroomVal: 20`). Sent with upload and save.
 */
export const DEFAULT_HEADROOM = 20;
export const HEADROOM_MIN = 0;
export const HEADROOM_MAX = 100;
export const HEADROOM_STEP = 10;

/**
 * Legacy's tooltip on the name field: "No special characters are allowed,
 * except for underscores (_) and hyphens (-)".
 */
export const PORTFOLIO_NAME_HINT =
  'No special characters are allowed, except for underscores (_) and hyphens (-)';

const PORTFOLIO_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

/** Legacy's bounds for a portfolio name (Upload: 3+, Save: 3–100). */
export const PORTFOLIO_NAME_MIN = 3;
export const PORTFOLIO_NAME_MAX = 100;

/**
 * Checked exactly as typed, as legacy does (FooterActions.vue:183-194,
 * UploadDownloadTemplate.vue:384-389): never trimmed, so a space anywhere —
 * leading and trailing included — fails the pattern.
 *
 * @returns {string | null} an error message, or null when the name is fine
 */
export function validatePortfolioName(name) {
  const value = String(name ?? '');
  if (!value) return 'Portfolio name is required';
  if (!PORTFOLIO_NAME_PATTERN.test(value)) return PORTFOLIO_NAME_HINT;
  if (value.length < PORTFOLIO_NAME_MIN) {
    return `Portfolio name must be at least ${PORTFOLIO_NAME_MIN} characters`;
  }
  if (value.length > PORTFOLIO_NAME_MAX) {
    return `Portfolio name must be at most ${PORTFOLIO_NAME_MAX} characters`;
  }
  return null;
}

/** Legacy: "Applied uniformly to all eligible prices". */
export const CONTRACT_DISCOUNT_HINT = 'Applied uniformly to all eligible prices';
export const CONTRACT_DISCOUNT_MAX = 99.9;

/**
 * Contract discount %: optional; 0 to 99.90 with at most 2 decimal places.
 * Same checks and messages as legacy updateMsaDiscount (ManageAccount.vue:771-781).
 *
 * @returns {string | null}
 */
export function validateContractDiscount(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 'Contract Discount%: Must be a number';
  if (n < 0) return 'Contract Discount%: Must be 0 or greater';
  if (n > CONTRACT_DISCOUNT_MAX) return 'Contract Discount%: Must be 99.90 or less';
  if (/\.\d{3,}$/.test(String(raw))) return 'Contract Discount%: Max 2 decimal places allowed';
  return null;
}

/**
 * Is the contract discount on screen still the one the portfolio was saved
 * with? Blank and 0 are the same value — Save sends a blank as 0, so a blank
 * saved portfolio comes back as 0 — and "5", "5.0" and 5 are all 5.
 */
export function sameContractDiscount(a, b) {
  const asNumber = (v) => (v === '' || v == null ? 0 : Number(v));
  return asNumber(a) === asNumber(b);
}

/**
 * EIA download templates (UploadDownloadTemplate.vue:116-119, 209-220, 334-344).
 * OCI has its own pair.
 *
 * Imported, as legacy does, rather than served from public/: Vite emits each
 * file with a hashed name and gives us its URL (vite.config.js
 * `assetsInclude`), so a missing file fails the build loudly.
 *
 * `filename` is the name the download is saved as — legacy's names, except
 * that legacy saved the first two WITHOUT an extension ("Sample_template",
 * "Self perf assessment"), which some browsers keep as an unopenable file.
 */
const TEMPLATES_STANDARD = Object.freeze([
  {
    id: 'instances',
    label: 'AWS/AZURE/GCP Template',
    href: eiaSampleTemplate,
    filename: 'Sample_template.xlsx',
  },
  {
    id: 'self-perf',
    label: 'Download Self Perf Template',
    href: eiaSelfPerfTemplate,
    filename: 'Self perf assessment.xlsx',
  },
]);

const TEMPLATES_OCI = Object.freeze([
  {
    id: 'oci-instances',
    label: 'Download OCI Template',
    href: ociTemplate,
    filename: 'OCI_Template.xlsx',
  },
  {
    id: 'oci-self-perf',
    label: 'Download OCI Self Perf Assessment',
    href: ociSelfPerfTemplate,
    filename: 'OCI_Self_Perf_Assessment.xlsx',
  },
]);

export function templatesFor(provider) {
  return String(provider ?? '').toUpperCase() === 'OCI' ? TEMPLATES_OCI : TEMPLATES_STANDARD;
}

/** Legacy accepts .xlsx only ("only .xlsx files are allowed"). */
export const UPLOAD_ACCEPT = '.xlsx';

export function isAllowedUpload(file) {
  return Boolean(file?.name) && file.name.toLowerCase().endsWith('.xlsx');
}

/* --- Add Instance --------------------------------------------------------- */

/** ManageAccount.vue:285-287. */
export const OS_OPTIONS = Object.freeze([
  { value: 'Linux', label: 'Linux' },
  { value: 'Windows', label: 'Windows' },
]);
export const DEFAULT_OS = 'Linux';

/**
 * ManageAccount.vue:282-284 and :583-588 — the label the user sees and the value
 * the API takes differ for the reserved terms.
 */
export const PRICING_MODEL_OPTIONS = Object.freeze([
  { value: 'ondemand', label: 'ondemand' },
  { value: 'reserved', label: 'reserved (1 year)' },
  { value: 'reserved_3yr', label: 'reserved (3 year)' },
]);

/** Field ids are the API's own keys — legacy sends formData under these names. */
export const FIELD = Object.freeze({
  REGION: 'region',
  INSTANCE_TYPE: 'instance type',
  OS: 'os_type',
  UUID: 'uuid',
  PRICING_MODEL: 'pricingModel',
  OCPU: 'ocpu',
  MEMORY: 'memory',
  MAX_MEM: 'max mem used',
  MAX_CPU: 'max cpu%',
  MAX_NETWORK_BW: 'max network bw',
  MAX_DISK_BW: 'max disk bw used',
  MAX_IOPS: 'max iops',
  UAVG: 'uavg',
  U95: 'u95',
});

/** An empty Add Instance form. */
export function emptyInstanceForm() {
  return {
    [FIELD.REGION]: '',
    [FIELD.INSTANCE_TYPE]: '',
    [FIELD.OS]: DEFAULT_OS,
    [FIELD.UUID]: '',
    [FIELD.PRICING_MODEL]: '',
    [FIELD.OCPU]: '',
    [FIELD.MEMORY]: '',
    [FIELD.MAX_MEM]: '',
    [FIELD.MAX_CPU]: '',
    [FIELD.MAX_NETWORK_BW]: '',
    [FIELD.MAX_DISK_BW]: '',
    [FIELD.MAX_IOPS]: '',
    [FIELD.UAVG]: '',
    [FIELD.U95]: '',
  };
}

/** OCI flex shapes take OCPU + Memory (ManageAccount.vue:288-291). */
export function isOciFlex(provider, instanceType) {
  return (
    String(provider ?? '').toUpperCase() === 'OCI' &&
    String(instanceType ?? '')
      .toLowerCase()
      .includes('flex')
  );
}

/**
 * Which fields must be filled before Add Instance — legacy EIArequiredFields
 * (ManageAccount.vue:628-642), plus OCPU / Memory for an OCI flex shape.
 */
export function requiredInstanceFields(provider, instanceType) {
  return [
    FIELD.REGION,
    FIELD.INSTANCE_TYPE,
    FIELD.PRICING_MODEL,
    FIELD.MAX_CPU,
    FIELD.MAX_MEM,
    FIELD.MAX_NETWORK_BW,
    FIELD.MAX_DISK_BW,
    FIELD.MAX_IOPS,
    ...(isOciFlex(provider, instanceType) ? [FIELD.OCPU, FIELD.MEMORY] : []),
  ];
}

/** @returns {Record<string, string>} field id -> message, empty when valid */
export function validateInstanceForm(form, provider) {
  const errors = {};
  for (const field of requiredInstanceFields(provider, form[FIELD.INSTANCE_TYPE])) {
    const value = form[field];
    if (value === '' || value == null) errors[field] = 'Required';
  }
  return errors;
}

/**
 * The instance row exactly as legacy sends it for EIA (ManageAccount.vue:679-697):
 * the API's own key names, cloud_csp upper-cased, os_type lower-cased, uavg/u95
 * as numbers defaulting to 0, OCPU / Memory only for an OCI flex shape.
 */
export function buildEiaInstance(form, provider) {
  const instance = {
    uuid: form[FIELD.UUID],
    cloud_csp: String(provider ?? '').toUpperCase(),
    ...(form[FIELD.REGION] ? { region: form[FIELD.REGION] } : {}),
    ...(form[FIELD.INSTANCE_TYPE] ? { 'instance type': form[FIELD.INSTANCE_TYPE] } : {}),
    'max cpu%': form[FIELD.MAX_CPU],
    'max mem used': form[FIELD.MAX_MEM],
    'max network bw': form[FIELD.MAX_NETWORK_BW],
    'max disk bw used': form[FIELD.MAX_DISK_BW],
    'max iops': form[FIELD.MAX_IOPS],
    pricingModel: form[FIELD.PRICING_MODEL],
    uavg: Number(form[FIELD.UAVG] || 0),
    u95: Number(form[FIELD.U95] || 0),
    os_type: String(form[FIELD.OS] || DEFAULT_OS).toLowerCase(),
  };
  if (isOciFlex(provider, form[FIELD.INSTANCE_TYPE])) {
    instance.ocpu = String(form[FIELD.OCPU]);
    instance.memory = String(form[FIELD.MEMORY]);
  }
  return instance;
}
